import React, { useState, useEffect } from 'react';
import { BarChart3, FileText, Database, TrendingUp, Clock, Flame, X } from 'lucide-react';
import '../styles/Library.css';

export const Analytics = ({ isVisible, onClose }) => {
    const [analytics, setAnalytics] = useState(null);
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('overview');

    const fetchAnalytics = async () => {
        setLoading(true);
        try {
            const fetchWithErrorHandling = async (url) => {
                const response = await fetch(url);
                if (!response.ok) {
                    console.warn(`Failed to fetch ${url}: ${response.status}`);
                    return null;
                }
                return response.json();
            };

            const [fileStats, hotDocsAnalytics, performance] = await Promise.all([
                fetchWithErrorHandling('https://api.casedb.co/analytics.php?action=file_stats'),
                fetchWithErrorHandling('https://api.casedb.co/analytics.php?action=hot_docs_analytics'),
                fetchWithErrorHandling('https://api.casedb.co/analytics.php?action=performance')
            ]);

            setAnalytics({
                fileStats: fileStats || { total_files: 0, hot_docs_count: 0, hot_docs_percentage: 0, by_type: [], by_folder: [] },
                hotDocsAnalytics: hotDocsAnalytics || { by_category: [], by_priority: [], most_accessed: [] },
                performance: performance || { table_stats: [], index_usage: [] }
            });
        } catch (error) {
            console.error('Error fetching analytics:', error);
            // Set default empty data on error
            setAnalytics({
                fileStats: { total_files: 0, hot_docs_count: 0, hot_docs_percentage: 0, by_type: [], by_folder: [] },
                hotDocsAnalytics: { by_category: [], by_priority: [], most_accessed: [] },
                performance: { table_stats: [], index_usage: [] }
            });
        }
        setLoading(false);
    };

    useEffect(() => {
        if (isVisible) {
            fetchAnalytics();
        }
    }, [isVisible]);

    if (!isVisible) return null;

    const formatBytes = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const formatNumber = (num) => {
        return num.toLocaleString();
    };

    return (
        <div className="analytics-overlay page-container" onClick={onClose}>
            <div className="analytics-modal" onClick={e => e.stopPropagation()}>
                <div className="analytics-header">
                    <h2><BarChart3 size={20} /> File System Analytics</h2>
                    <span onClick={onClose} className="close-btn"><X size={16} /></span>
                </div>

                <div className="analytics-tabs">
                    <button 
                        className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
                        onClick={() => setActiveTab('overview')}
                    >
                        <Database size={16} /> Overview
                    </button>
                    <button 
                        className={`tab ${activeTab === 'hotdocs' ? 'active' : ''}`}
                        onClick={() => setActiveTab('hotdocs')}
                    >
                        <Flame size={16} /> Hot Docs
                    </button>
                    <button 
                        className={`tab ${activeTab === 'performance' ? 'active' : ''}`}
                        onClick={() => setActiveTab('performance')}
                    >
                        <TrendingUp size={16} /> Performance
                    </button>
                </div>

                <div className="analytics-content">
                    {loading ? (
                        <div className="analytics-loading">Loading analytics...</div>
                    ) : analytics ? (
                        <>
                            {activeTab === 'overview' && (
                                <div className="analytics-tab-content">
                                    <div className="stats-grid">
                                        <div className="stat-card">
                                            <FileText size={24} />
                                            <div className="stat-info">
                                                <h3>{formatNumber(analytics?.fileStats?.total_files)}</h3>
                                                <p>Total Files</p>
                                            </div>
                                        </div>
                                        <div className="stat-card">
                                            <Flame size={24} />
                                            <div className="stat-info">
                                                <h3>{formatNumber(analytics?.fileStats?.hot_docs_count)}</h3>
                                                <p>Hot Docs ({analytics?.fileStats?.hot_docs_percentage}%)</p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="analytics-section">
                                        <h3>Files by Type</h3>
                                        <div className="file-types">
                                            {analytics?.fileStats?.by_type?.length > 0 ? (
                                                analytics.fileStats.by_type.map(type => (
                                                    <div key={type.file_type} className="file-type-row">
                                                        <span className="file-type-name">{type.file_type}</span>
                                                        <span className="file-type-count">{formatNumber(type.count)}</span>
                                                    </div>
                                                ))
                                            ) : (
                                                <div className="no-data">No file type data available</div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="analytics-section">
                                        <h3>Top Folders</h3>
                                        <div className="folder-stats">
                                            {analytics?.fileStats?.by_folder?.length > 0 ? (
                                                analytics.fileStats.by_folder.map(folder => (
                                                    <div key={folder.folder} className="folder-row">
                                                        <span className="folder-name">{folder.folder}</span>
                                                        <span className="folder-count">{formatNumber(folder.count)}</span>
                                                    </div>
                                                ))
                                            ) : (
                                                <div className="no-data">No folder data available</div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {activeTab === 'hotdocs' && (
                                <div className="analytics-tab-content">
                                    <div className="analytics-section">
                                        <h3>Hot Docs by Category</h3>
                                        <div className="category-stats">
                                            {analytics?.hotDocsAnalytics?.by_category?.length > 0 ? (
                                                analytics.hotDocsAnalytics.by_category.map(cat => (
                                                    <div key={cat.category} className="category-row">
                                                        <span className="category-name">{cat.category}</span>
                                                        <span className="category-count">{formatNumber(cat.count)}</span>
                                                    </div>
                                                ))
                                            ) : (
                                                <div className="no-data">No category data available</div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="analytics-section">
                                        <h3>Hot Docs by Priority</h3>
                                        <div className="priority-stats">
                                            {analytics?.hotDocsAnalytics?.by_priority?.length > 0 ? (
                                                analytics.hotDocsAnalytics.by_priority.map(pri => (
                                                    <div key={pri.priority} className={`priority-row priority-${pri.priority}`}>
                                                        <span className="priority-name">{pri.priority}</span>
                                                        <span className="priority-count">{formatNumber(pri.count)}</span>
                                                    </div>
                                                ))
                                            ) : (
                                                <div className="no-data">No priority data available</div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="analytics-section">
                                        <h3>Most Accessed Hot Docs</h3>
                                        <div className="accessed-stats">
                                            {analytics?.hotDocsAnalytics?.most_accessed?.length > 0 ? (
                                                analytics.hotDocsAnalytics.most_accessed.slice(0, 5).map((doc, idx) => (
                                                    <div key={doc.file_key} className="accessed-row">
                                                        <span className="access-rank">#{idx + 1}</span>
                                                        <span className="access-name">{doc.name || 'Unknown'}</span>
                                                        <span className="access-count">{formatNumber(doc.access_count)} views</span>
                                                    </div>
                                                ))
                                            ) : (
                                                <div className="no-data">No access data available</div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {activeTab === 'performance' && (
                                <div className="analytics-tab-content">
                                    <div className="analytics-section">
                                        <h3>Table Statistics</h3>
                                        <div className="table-stats">
                                            {analytics?.performance?.table_stats?.length > 0 ? (
                                                analytics.performance.table_stats.map(table => (
                                                    <div key={table.table} className="table-row">
                                                        <span className="table-name">{table.table}</span>
                                                        <div className="table-details">
                                                            <span>{formatNumber(table.rows)} rows</span>
                                                            <span>{formatBytes(table.total_size)}</span>
                                                        </div>
                                                    </div>
                                                ))
                                            ) : (
                                                <div className="no-data">No table statistics available</div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="analytics-section">
                                        <h3>Database Indexes</h3>
                                        <div className="index-stats">
                                            {analytics?.performance?.index_usage?.length > 0 ? (
                                                analytics.performance.index_usage.map((index, idx) => (
                                                    <div key={idx} className="index-row">
                                                        <span className="index-name">{index.index}</span>
                                                        <span className="index-column">{index.column}</span>
                                                        {index.unique && <span className="index-unique">UNIQUE</span>}
                                                    </div>
                                                ))
                                            ) : (
                                                <div className="no-data">No index information available</div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="analytics-error">Failed to load analytics</div>
                    )}
                </div>

                <div className="analytics-footer">
                    <button onClick={fetchAnalytics} disabled={loading}>
                        <Clock size={16} /> Refresh Data
                    </button>
                </div>
            </div>
        </div>
    );
};

export default Analytics;
